import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";

// Pages
import LandingPage    from "./pages/LandingPage";
import Login          from "./pages/Login";
import Signup         from "./pages/Signup";
import Onboarding     from "./pages/Onboarding";
import Pricing        from "./pages/Pricing";
import Dashboard      from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel  from "./pages/PaymentCancel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Shared spinner ─────────────────────────────────────────────────────
const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9]">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin" />
      <span className="text-xs text-slate-400 font-medium font-['Outfit']">Loading ORDO AI…</span>
    </div>
  </div>
);

// ── fetchMe ────────────────────────────────────────────────────────────
const fetchMe = async () => {
  const sid = localStorage.getItem("session_id");
  if (!sid) return null;
  try {
    const res = await fetch(`${API}/auth/me`, {
      credentials: "include",
      headers: sid ? { "X-Session-ID": sid } : {},
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

// ── PrivateRoute ───────────────────────────────────────────────────────
const PrivateRoute = ({ children }) => {
  const [status, setStatus] = useState("loading");
  useEffect(() => {
    fetchMe()
      .then((user) => setStatus(user ? "ok" : "fail"))
      .catch(() => setStatus("fail"));
  }, []);
  if (status === "loading") return <Spinner />;
  return status === "ok" ? children : <Navigate to="/login" replace />;
};

// ── OnboardingRoute ────────────────────────────────────────────────────
const OnboardingRoute = ({ children }) => {
  const [status, setStatus]     = useState("loading");
  const [redirect, setRedirect] = useState(null);
  useEffect(() => {
    fetchMe().then((user) => {
      if (!user)                     { setRedirect("/login"); }
      else if (user.onboarding_complete) { setRedirect("/dashboard"); }
      else                           { setStatus("ok"); }
    }).catch(() => setRedirect("/login"));
  }, []);
  if (status === "loading" && !redirect) return <Spinner />;
  if (redirect) return <Navigate to={redirect} replace />;
  return children;
};

// ── AdminRoute ─────────────────────────────────────────────────────────
const AdminRoute = ({ children }) => {
  const [status, setStatus] = useState("loading");
  useEffect(() => {
    fetchMe().then((user) => {
      if (!user)                  setStatus("fail");
      else if (user.role === "admin") setStatus("ok");
      else                        setStatus("deny");
    }).catch(() => setStatus("fail"));
  }, []);
  if (status === "loading") return <Spinner />;
  if (status === "deny")    return <Navigate to="/dashboard" replace />;
  return status === "ok" ? children : <Navigate to="/login" replace />;
};

// ── GoogleAuthSuccess ──────────────────────────────────────────────────
// Backend redirects here with ?session_id=xxx after Google OAuth.
const GoogleAuthSuccess = () => {
  const [redirect, setRedirect] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid    = params.get("session_id");
    const code   = params.get("code");

    if (sid) {
      localStorage.setItem("session_id", sid);
      fetchMe().then((user) => {
        if (!user) {
          setRedirect("/login");
        } else if (user.role === "admin") {
          setRedirect("/admin");
        } else if (!user.onboarding_complete) {
          setRedirect("/onboarding");
        } else if (user.has_active_subscription || user.subscription_status === "trialing") {
          setRedirect("/dashboard");
        } else {
          setRedirect("/onboarding");
        }
      }).catch(() => setRedirect("/login"));
    } else if (code) {
      // Fallback: forward code to backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://rankfirst-production.up.railway.app";
      window.location.href = `${backendUrl}/api/auth/google/callback?code=${code}`;
    } else {
      setRedirect("/login");
    }
  }, []);

  if (!redirect) return <Spinner />;
  return <Navigate to={redirect} replace />;
};

// ── LoginRedirect ──────────────────────────────────────────────────────
// Called after email login — reads user and sends to right place.
const LoginRedirect = () => {
  const [redirect, setRedirect] = useState(null);
  useEffect(() => {
    fetchMe().then((user) => {
      if (!user) {
        setRedirect("/login");
      } else if (user.role === "admin") {
        setRedirect("/admin");
      } else if (!user.onboarding_complete) {
        setRedirect("/onboarding");
      } else if (user.has_active_subscription || user.subscription_status === "trialing") {
        setRedirect("/dashboard");
      } else {
        setRedirect("/onboarding");
      }
    }).catch(() => setRedirect("/login"));
  }, []);
  if (!redirect) return <Spinner />;
  return <Navigate to={redirect} replace />;
};

// ── App ────────────────────────────────────────────────────────────────
const App = () => {
  return (
    <Router>
      <Toaster position="top-right" richColors closeButton />
      <Routes>

        {/* ── PUBLIC ── */}
        <Route path="/"        element={<LandingPage />} />
        <Route path="/login"   element={<Login />} />
        <Route path="/signup"  element={<Signup />} />
        <Route path="/pricing" element={<Pricing />} />

        {/* ── AUTH CALLBACKS ── */}
        <Route path="/auth/success"         element={<GoogleAuthSuccess />} />
        <Route path="/auth/google/callback" element={<GoogleAuthSuccess />} />
        <Route path="/auth/redirect"        element={<LoginRedirect />} />

        {/* ── ONBOARDING ── */}
        <Route path="/onboarding"
          element={<OnboardingRoute><Onboarding /></OnboardingRoute>}
        />

        {/* ── PROTECTED ── */}
        <Route path="/dashboard"
          element={<PrivateRoute><Dashboard /></PrivateRoute>}
        />
        <Route path="/settings"
          element={<PrivateRoute><Navigate to="/dashboard?section=settings" replace /></PrivateRoute>}
        />
        <Route path="/payment/success"
          element={<PrivateRoute><PaymentSuccess /></PrivateRoute>}
        />
        <Route path="/payment/cancel"
          element={<PrivateRoute><PaymentCancel /></PrivateRoute>}
        />

        {/* ── ADMIN ── */}
        <Route path="/admin"
          element={<AdminRoute><Navigate to="/admin/dashboard" replace /></AdminRoute>}
        />
        <Route path="/admin/dashboard"
          element={<AdminRoute><AdminDashboard /></AdminRoute>}
        />

        {/* ── CATCH-ALL ── */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
    </Router>
  );
};

export default App;
